import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingCart, Trash2, TicketPercent, CheckCircle2, Landmark } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import { api, errMsg, eur, fmtDate } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { InvoiceBox, invoiceValid } from "../../components/site/InvoiceBox";

export const cartChanged = () => window.dispatchEvent(new Event("aa-cart"));

const Lines = ({ lines, testid }) => {
  const { t } = useLang();
  return (
    <table className="mt-3 w-full text-sm" data-testid={testid}>
      <thead className="text-left text-xs uppercase tracking-wider text-slate-400">
        <tr><th className="py-1 font-semibold">{t("cart_product")}</th><th className="py-1 text-right font-semibold">{t("cart_net")}</th><th className="py-1 text-right font-semibold">{t("cart_tax")}</th><th className="py-1 text-right font-semibold">{t("cart_gross")}</th></tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {lines.map((l, i) => (
          <tr key={i}>
            <td className="py-2 pr-2">{l.label}{l.discount > 0 && <span className="block text-xs font-semibold text-emerald-600">{t("cart_discount")} −{eur(l.discount)} ({t("cart_list_price")} {eur(l.list_net)})</span>}</td>
            <td className="py-2 text-right">{eur(l.net)}</td><td className="py-2 text-right text-slate-500">{l.tax_rate}% · {eur(l.tax)}</td><td className="py-2 text-right font-semibold">{eur(l.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const Totals = ({ totals }) => {
  const { t } = useLang();
  return (
    <div className="space-y-1 rounded-3xl bg-[#0A2540] p-6 text-sm text-white" data-testid="cart-totals">
      {totals.discount > 0 && <p className="flex justify-between text-white/70"><span>{t("cart_net_before")}</span><span>{eur(totals.net + totals.discount)}</span></p>}
      {totals.discount > 0 && <p className="flex justify-between text-emerald-300"><span>{t("cart_discount")}</span><span data-testid="cart-total-discount">−{eur(totals.discount)}</span></p>}
      <p className="flex justify-between"><span>{t("cart_total_net")}</span><span data-testid="cart-total-net">{eur(totals.net)}</span></p>
      <p className="flex justify-between"><span>{t("cart_total_tax")}</span><span data-testid="cart-total-tax">{eur(totals.tax)}</span></p>
      <p className="flex justify-between border-t border-white/20 pt-3 font-display text-2xl font-extrabold"><span>{t("total")}</span><span data-testid="cart-total-gross">{eur(totals.gross)}</span></p>
    </div>
  );
};

const PayMethods = ({ methods, value, onChange }) => {
  const { t } = useLang();
  if (!methods?.length) return null;
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="cart-payment-methods">
      <p className="font-semibold text-[#0A2540]">{t("pm_choose")}</p>
      <div className="mt-3 space-y-2">
        {methods.map((m) => (
          <label key={m} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input type="radio" name="pm" checked={value === m} onChange={() => onChange(m)} data-testid={`cart-pm-${m.toLowerCase().replace(/[^a-z]+/g, "-")}`} />{t(`pm_${m.toLowerCase().replace(/[^a-z]+/g, "_")}`)}
          </label>
        ))}
      </div>
    </div>
  );
};

const OrderBlock = ({ order }) => {
  const { t } = useLang();
  const c = order.companies?.[0];
  const bank = order.show_bank !== false;
  return (
    <div className="mt-10 border-t border-slate-200 pt-8" data-testid={`order-block-${order.number}`}>
      <p className="text-slate-600">{t("order_number")}: <b data-testid="order-number">{order.number}</b> · {t("order_sim_note")}</p>
      <div className="mt-6 space-y-4">{order.items.map((it) => (
        <div key={it.id} className="rounded-3xl border border-slate-200 bg-white p-6"><p className="font-display text-lg font-bold text-[#0A2540]">{it.event_title} · {it.sailor}</p><Lines lines={it.lines} testid={`order-lines-${it.id}`} /></div>))}
      </div>
      <div className="mt-6"><Totals totals={order.totals} /></div>
      <div className="mt-6 flex items-start gap-3 rounded-3xl border-2 border-dashed border-[#00A8B5] p-6 text-sm text-slate-600" data-testid="order-payment-position">
        <Landmark className="shrink-0 text-[#00A8B5]" />
        <p><b className="text-[#0A2540]">{t("order_single_payment")}</b><br />
          {bank ? <>{t(order.other_amount > 0 ? "order_pay_part" : "order_pay_text")} <b>{order.number}</b> – {eur(order.bank_amount ?? order.totals.gross)}</> : <span data-testid="order-pay-soon">{t("pay_details_soon")} – {eur(order.totals.gross)}</span>}
          {bank && order.other_amount > 0 && <span className="mt-1 block font-semibold" data-testid="order-pay-soon-part">{order.other_is_charter ? `${t("pay_charter_label")}: ` : ""}{t("pay_details_soon")} – {eur(order.other_amount)}</span>}
          {c && <span className="mt-2 block" data-testid={`order-company-${order.number}`}>{t("organizer")}: <b>{c.name}</b>{c.vat_number ? ` · VAT ${c.vat_number}` : ""}<br />
            {[c.address, [c.zip, c.city].filter(Boolean).join(" "), c.country].filter(Boolean).join(", ")}{c.email ? ` · ${c.email}` : ""}
            {bank && c.iban && <span className="block font-mono" data-testid="order-iban">IBAN <b>{c.iban}</b>{c.swift ? ` · SWIFT/BIC ${c.swift}` : ""}</span>}</span>}</p>
      </div>
    </div>
  );
};

const Done = ({ result }) => {
  const { t } = useLang();
  const orders = result.orders || [result];
  return (
    <div className="container-x max-w-3xl pb-24 pt-36" data-testid="order-done">
      <CheckCircle2 size={48} className="text-emerald-500" />
      <h1 className="mt-4 font-display text-4xl font-extrabold text-[#0A2540] dark:text-white">{t("order_done")}</h1>
      {orders.length > 1 && <p className="mt-2 font-semibold text-[#0A2540]" data-testid="order-split-note">{t("order_split_done").replace("{n}", orders.length)}</p>}
      <p className="mt-1 text-sm text-slate-500" data-testid="order-email-note">{t("order_email_sent")}</p>
      {orders.map((o) => <OrderBlock key={o.id} order={o} />)}
      <Link to="/account/registrations" className="btn-buoy mt-8" data-testid="order-go-registrations">{t("acc_regs")}</Link>
    </div>
  );
};

export default function Cart() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [cart, setCart] = useState(null);
  const [code, setCode] = useState("");
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bill, setBill] = useState(null);
  const [pm, setPm] = useState("");
  useEffect(() => {
    if (cart?.payment_methods?.length && !cart.payment_methods.includes(pm)) setPm(cart.payment_methods.length === 1 ? cart.payment_methods[0] : "");
  }, [cart, pm]);
  useEffect(() => {
    if (user === false) nav("/login?next=/cart");
    if (user) api.get("/me/cart").then((r) => { setCart(r.data); setBill((b) => b || { invoice: false, mode: "parent", data: {}, ...(r.data.billing_pref || {}) }); }).catch((e) => toast.error(errMsg(e)));
  }, [user, nav]);
  const call = async (fn) => {
    try { setCart((await fn()).data); cartChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const checkout = async () => {
    if (!invoiceValid(bill)) return toast.error(t("inv_required"));
    if (cart.payment_methods?.length && !pm) return toast.error(t("pm_choose"));
    setBusy(true);
    try { const { data } = await api.post("/me/cart/checkout", { ...bill, payment_method: pm }); setOrder(data); cartChanged(); } catch (e) { toast.error(errMsg(e)); api.get("/me/cart").then((r) => setCart(r.data)).catch(() => {}); } finally { setBusy(false); }
  };
  if (order) return <Done result={order} />;
  if (!cart) return <div className="h-screen" />;
  return (
    <div className="container-x pb-24 pt-36" data-testid="cart-page">
      <p className="eyebrow">{t("cart")}</p>
      <h1 className="mt-3 font-display text-4xl font-extrabold text-[#0A2540] sm:text-5xl dark:text-white">{t("cart_title")}</h1>
      {cart.discount_notice && (
        <div className="mt-6 flex items-start justify-between gap-4 rounded-2xl bg-amber-50 p-4 text-sm font-semibold text-amber-800" role="alert" data-testid="cart-discount-notice">
          <span>{t("cart_discount_removed").replace("{code}", cart.discount_notice.code).replace("{state}", t(`discount_${cart.discount_notice.state}`))}</span>
          <button onClick={() => call(() => api.delete("/me/cart/discount"))} className="shrink-0 underline" data-testid="cart-discount-notice-dismiss">OK</button>
        </div>
      )}
      {!cart.items.length ? (
        <div className="mt-10 rounded-3xl border border-dashed border-slate-300 p-12 text-center" data-testid="cart-empty">
          <ShoppingCart className="mx-auto text-slate-300" size={40} /><p className="mt-3 text-slate-500">{t("cart_empty")}</p>
          <Link to="/clinics" className="btn-buoy mt-6" data-testid="cart-browse">{t("see_all")}</Link>
        </div>
      ) : (
        <div className="mt-10 grid gap-8 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-8">
            {cart.items.map((it) => (
              <div key={it.id} className="rounded-3xl border border-slate-200 bg-white p-6" data-testid={`cart-item-${it.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-lg font-bold text-[#0A2540]">{it.event_title || it.event_id}</p>
                    {!it.error && <p className="text-sm text-slate-500">{it.sailor} · {it.class_name} · {fmtDate(it.start_date, lang)} – {fmtDate(it.end_date, lang)}{it.organizer ? ` · ${t("organizer")}: ${it.organizer}` : ""}{it.status === "waitlist" ? ` · ${t("waitlist")}` : ""}</p>}
                    {it.error && <p className="text-sm font-semibold text-rose-600" data-testid={`cart-item-error-${it.id}`}>{it.error}</p>}
                  </div>
                  <button onClick={() => call(() => api.delete(`/me/cart/items/${it.id}`))} className="text-slate-400 hover:text-rose-600" title={t("remove")} data-testid={`cart-remove-${it.id}`}><Trash2 size={18} /></button>
                </div>
                {!it.error && <Lines lines={it.lines} testid={`cart-lines-${it.id}`} />}
              </div>
            ))}
            <Link to="/clinics" className="inline-block text-sm font-semibold text-[#FF5722] underline" data-testid="cart-continue">{t("cart_continue")}</Link>
          </div>
          <div className="space-y-4 lg:col-span-4">
            <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="cart-discount-box">
              <p className="flex items-center gap-2 font-semibold text-[#0A2540]"><TicketPercent size={18} />{t("cart_discount_code")}</p>
              {cart.discount ? (
                <div className="mt-3 flex items-center justify-between rounded-2xl bg-emerald-50 p-3 text-sm" data-testid="cart-discount-applied">
                  <span><b>{cart.discount.code}</b> · {cart.discount.kind === "percent" ? `${cart.discount.value}%` : eur(cart.discount.value)}</span>
                  <button onClick={() => call(() => api.delete("/me/cart/discount"))} className="text-xs font-semibold text-rose-600 underline" data-testid="cart-discount-remove">{t("remove")}</button>
                </div>
              ) : (
                <div className="mt-3 flex gap-2">
                  <Input value={code} maxLength={8} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="XXXXXXXX" className="h-11 rounded-xl font-mono tracking-widest" data-testid="cart-discount-input" />
                  <button onClick={() => call(() => api.post("/me/cart/discount", { code }))} disabled={code.length !== 8} className="btn-buoy !px-4 !py-2 disabled:opacity-50" data-testid="cart-discount-apply">{t("apply")}</button>
                </div>
              )}
              <p className="mt-2 text-xs text-slate-500">{t("cart_discount_note")}</p>
              {cart.discount?.event_title && <p className="mt-1 text-xs font-semibold text-[#00A8B5]" data-testid="cart-discount-event-note">{t("cart_discount_event_note").replace("{event}", cart.discount.event_title)}</p>}
            </div>
            <Totals totals={cart.totals} />
            {bill && <InvoiceBox value={bill} onChange={setBill} parent={cart.parent_billing || {}} />}
            <PayMethods methods={cart.payment_methods} value={pm} onChange={setPm} />
            <button onClick={checkout} disabled={busy || cart.items.some((i) => i.error)} className="btn-buoy w-full justify-center disabled:opacity-60" data-testid="cart-checkout">{t("cart_checkout")}</button>
            {cart.split_orders > 1 ? <p className="rounded-2xl bg-amber-50 p-3 text-xs font-semibold text-amber-800" data-testid="cart-split-notice">{t("cart_split_note").replaceAll("{n}", cart.split_orders)}</p>
              : <p className="text-xs text-slate-500">{t("cart_single_payment_note")}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
